import type { Pattern } from '../../contexts/PatternContext'

/**
 * Serializes the open pattern into the narrow authoring form that
 * propose_pattern_edit accepts — the same schema the sessions' save_pattern
 * uses. The sentinel block carries THIS form, so the model reads and writes
 * one schema and never touches raw Pattern JSON (the server translates back).
 *
 * Inversions of the server's mapConditionGroup:
 * - ids ride as `key` (echoed keys survive the round-trip verbatim)
 * - composite params ride along so the server can honor the pattern's pinned
 *   version and validate composites the user doesn't own (shared patterns)
 * - unary conditions drop their vestigial value RHS (a faithful echo of it
 *   would fail server validation)
 * - unset LHS/conditionType fields are omitted — work-in-progress conditions
 *   round-trip under the server's allowIncomplete tolerance
 * - group chart fields are emitted only where they differ from the parent's
 * - settings unfold to validityWindow / prerequisite keys / previousCandle
 */

// Mirrors CONDITION_TYPES on the server: these take no RHS
const UNARY_CONDITION_TYPES = new Set(['moving up', 'moving down'])

interface ChartDefaults {
  symbol?: string
  periodicity?: number
  timeframe?: string
}

const candleToForm = (candle: any): Record<string, unknown> | undefined => {
  if (!candle || typeof candle !== 'object') return undefined
  const offset = typeof candle.offset === 'number' ? candle.offset : 0
  const lookback = candle.lookback
  if (!lookback && !offset) return undefined
  const out: Record<string, unknown> = { offset }
  if (lookback && typeof lookback === 'object') {
    out.lookback = {
      count: lookback.count,
      mode: lookback.mode,
      ...(lookback.needed !== undefined ? { needed: lookback.needed } : {}),
    }
  }
  return out
}

const paramsToForm = (side: any): Record<string, unknown> | undefined => {
  const params = side?.indicator?.params
  return params && typeof params === 'object' && Object.keys(params).length > 0 ? params : undefined
}

const conditionToForm = (c: any): Record<string, unknown> => {
  const cond = c?.condition ?? {}
  const out: Record<string, unknown> = { key: c?.id }
  if (cond.LHS?.indicator?.name) out.lhsIndicator = cond.LHS.indicator.name
  if (cond.LHS?.field) out.lhsField = cond.LHS.field
  if (cond.conditionType) out.conditionType = cond.conditionType
  const lhsParams = paramsToForm(cond.LHS)
  if (lhsParams) out.lhsParams = lhsParams
  const lhsCandle = candleToForm(cond.LHS?.candle)
  if (lhsCandle) out.lhsCandle = lhsCandle

  // Unary types carry a vestigial value RHS in some stored patterns — dead
  // weight the server would reject if echoed
  const rhsName = UNARY_CONDITION_TYPES.has(cond.conditionType)
    ? undefined
    : cond.RHS?.indicator?.name
  if (rhsName === 'value') {
    const value = Number(cond.RHS?.value)
    out.rhsValue = Number.isFinite(value) ? value : 0
  } else if (rhsName) {
    out.rhsIndicator = rhsName
    if (cond.RHS?.field) out.rhsField = cond.RHS.field
    const rhsParams = paramsToForm(cond.RHS)
    if (rhsParams) out.rhsParams = rhsParams
    const rhsCandle = candleToForm(cond.RHS?.candle)
    if (rhsCandle) out.rhsCandle = rhsCandle
  }

  if (cond.threshold !== undefined) out.threshold = cond.threshold
  const validityCandles = c?.settings?.validityWindow?.candles
  if (typeof validityCandles === 'number') out.validityWindow = validityCandles
  const prereqs = c?.settings?.prerequisites
  if (Array.isArray(prereqs) && prereqs.length > 0) {
    const byType = (type: string) =>
      prereqs
        .filter((p: any) => (p?.type ?? 'condition') === type && typeof p?.id === 'string')
        .map((p: any) => p.id)
    const conditionKeys = byType('condition')
    const groupKeys = byType('group')
    if (conditionKeys.length > 0) out.prerequisiteKeys = conditionKeys
    if (groupKeys.length > 0) out.prerequisiteGroupKeys = groupKeys
  }
  if (c?.settings?.previousCandle) out.previousCandle = c.settings.previousCandle
  return out
}

const groupToForm = (g: any, parent: ChartDefaults): Record<string, unknown> => {
  const conditions = Array.isArray(g?.conditions) ? g.conditions : []
  const nested = Array.isArray(g?.conditionGroups) ? g.conditionGroups : []
  const out: Record<string, unknown> = {
    key: g?.id,
    title: g?.groupTitle,
    conditions: conditions.map(conditionToForm),
  }
  if (typeof g?.count === 'number' && g.count !== conditions.length + nested.length) {
    out.count = g.count
  }
  const own: ChartDefaults = {
    symbol: g?.symbol ?? parent.symbol,
    periodicity: g?.periodicity ?? parent.periodicity,
    timeframe: g?.timeframe ?? parent.timeframe,
  }
  if (own.symbol !== parent.symbol) out.symbol = own.symbol
  if (own.periodicity !== parent.periodicity) out.periodicity = own.periodicity
  if (own.timeframe !== parent.timeframe) out.timeframe = own.timeframe
  if (nested.length > 0) {
    out.conditionGroups = nested.map((n: any) => groupToForm(n, own))
  }
  return out
}

export const patternToAuthoringForm = (pattern: Pattern): Record<string, unknown> => {
  const chart = (pattern as any)?.trigger?.chartDetails ?? {}
  const defaults: ChartDefaults = {
    symbol: chart.symbol,
    periodicity: chart.periodicity,
    timeframe: chart.timeframe,
  }
  return {
    name: pattern.name,
    ...(typeof (pattern as any).message === 'string' ? { message: (pattern as any).message } : {}),
    symbol: defaults.symbol,
    periodicity: defaults.periodicity,
    timeframe: defaults.timeframe,
    buyConditionGroups: (pattern.buyConditionGroups ?? []).map((g: any) =>
      groupToForm(g, defaults)
    ),
    sellConditionGroups: (pattern.sellConditionGroups ?? []).map((g: any) =>
      groupToForm(g, defaults)
    ),
  }
}
